import { ReactNode, useEffect, useState } from 'react';
import { AuthCheckLogin, AuthDataProvider, AuthProvider } from 'neon-auth0';
import { config } from '../../config';
import { Loader } from 'neon-design-system';
import { UnauthorisedAccessError } from '../../pages/UnauthorisedAccessError/UnauthorisedAccessError';
import { logEvent } from '../../utils/logger';
import { useSessionData } from '../../contexts/SessionData';
import { redirectToUrl } from '../../utils/redirectToUrl';
import { useMfa } from '../../providers/MfaProvider';
import { Auth0Client } from '@auth0/auth0-spa-js';
import jwt from 'jwt-decode';
import { ServerErrorView } from '../ServerErrorView/ServerErrorView';

interface Props {
  children: ReactNode;
}

export const Auth = ({ children }: Props) => {
  const [isReady, setIsReady] = useState(false);
  const [scopeValidationError, setScopeValidationError] = useState(false);
  const [isUnvettedUser, setIsUnvettedUser] = useState(false);
  const appInfo = useSessionData();
  const { isMfa, scope } = useMfa();
  const onRedirectCallback = (appState: any): void => {
    window.history.replaceState(
      {},
      document.title,
      appState && appState.targetUrl ? appState.targetUrl : config.basePath
    );
    //if the user is redirected to the app after login, that means scopes has  already been checked
    setIsReady(true);
  };

  useEffect(() => {
    if (isReady) return;
    // get the token silently first without 'portfolio' auth0 scope to avoid triggering MFA
    // if they only have 'insights:content-reader-trial' scope they are considered unvetted
    async function getToken() {
      const auth0 = await new Auth0Client({
        domain: config.auth0.domain,
        clientId: config.auth0.clientId,
        authorizationParams: {
          redirect_uri: config.auth0.redirectUri,
          audience: config.auth0.audience
        }
      });

      try {
        return await auth0.getTokenSilently();
      } catch (e) {
        await auth0.loginWithRedirect({
          authorizationParams: {
            redirect_uri: config.auth0.redirectUri
          }
        });
      }
    }

    getToken()
      .then((token) => {
        if (!token) return;
        const decodedToken: any = jwt(token);
        const permissions = decodedToken.permissions;
        const curIsUnvettedUser =
          !permissions.length ||
          (permissions.length === 1 && permissions[0] === 'insights:content-reader-trial');
        if (curIsUnvettedUser && !isUnvettedUser) {
          // user is unvetted, so set the state to true
          setIsUnvettedUser(true);
        }

        //returns user to the same page but without url query params
        window.history.replaceState(
          {},
          document.title,
          window.location.origin + window.location.pathname
        );
        setIsReady(true);
      })
      .catch((e) => {
        logEvent({
          event: 'auth_scope_validation_failed',
          level: 'Error',
          error: e instanceof Error ? e.message : String(e),
          errorStack: e instanceof Error ? e.stack : undefined
        });
        setIsReady(true);
        // scope check failed, interrupt
        setScopeValidationError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  if (!isReady) {
    return <Loader />;
  }

  if (scopeValidationError) {
    return <ServerErrorView />;
  }

  if (isUnvettedUser) {
    return <UnauthorisedAccessError />;
  }

  return (
    <AuthProvider
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      redirectUri={config.auth0.redirectUri}
      returnToUri={config.auth0.returnToUri}
      audience={config.auth0.audience}
      onRedirectCallback={onRedirectCallback}
      scope={scope}
    >
      {/* Remove loginUri prop to test locally on IE11 or Safari */}
      <AuthCheckLogin
        loginUri={isMfa ? redirectToUrl(appInfo.sessionId, isMfa)?.href : undefined}
        loader={<Loader />}
        errorComponentCallback={UnauthorisedAccessError}
        appInfo={appInfo}
        addLogEntry={(param) => {
          logEvent({ event: 'auth_success', ...param });
        }}
        invalidScopeMessage="User is not permissioned to use this system"
      >
        <AuthDataProvider>{children}</AuthDataProvider>
      </AuthCheckLogin>
    </AuthProvider>
  );
};
